import { useMemo, useState, type FormEvent, type ReactNode } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Modal } from '../Modal';
import { Field, FormShell, Select, TextArea, type FieldErrors } from '../FormShell';
import { ROI_LINE_KIND_LABELS, ROI_PERIOD_LABELS, type BoardDto, type CardDto, type CardUpsertDto, type RoiLine } from '../../lib/types';
import { boardQueryKey, createCard, deleteCard, problemDetail, updateCard } from '../../lib/workApi';
import { formatBuildCost, formatFullDate, formatMoney, formatRoi } from '../../lib/format';
import { buildCost, isComputedLabor, lineAmount } from '../../lib/roi';
import { saveErrorState } from './formErrors';
import { RoiCalculator } from './RoiCalculator';
import { draftsFromLines, lineFromDraft, type RoiLineDraft } from './roiDraft';
import { MONTHS } from '../../lib/months';
import { MonthPill } from './CardTile';

interface CardDialogProps {
  board: BoardDto;
  /** null = create a new card. */
  cardId: number | null;
  canWrite: boolean;
  onClose: () => void;
  /** Swap the dialog to another card (dependency link). Unsaved edits are discarded. */
  onShowCard: (cardId: number) => void;
}

const FORM_ID = 'card-form';
const FIELDS = [
  'workStreamId', 'title', 'description', 'roiOneTime', 'roiAnnual', 'roiExplanation',
  'workersNeeded', 'estimateWeeks', 'productOwnerName', 'dependencyCardId', 'tokensCost', 'additionalCost',
  'targetMonth',
] as const;

const monthOptions = [{ value: '', label: 'None' }, ...MONTHS.map((m) => ({ value: String(m.n), label: m.long }))];

const btnSecondary = 'px-3 py-1.5 rounded border border-slate-300 text-slate-700 hover:bg-slate-100';
const btnPrimary = 'px-3 py-1.5 rounded bg-atg-blue text-white hover:bg-atg-navy disabled:opacity-50';
const linkButton = 'text-atg-blue hover:underline font-semibold';

function numText(n: number | null | undefined): string {
  return n === null || n === undefined ? '' : String(n);
}

function numOrNull(s: string): number | null {
  return s.trim() === '' ? null : Number(s);
}

function textOrNull(s: string): string | null {
  const t = s.trim();
  return t === '' ? null : t;
}

/**
 * New / edit / view dialog for a card. The parent keys this component by card id, so swapping
 * cards via the dependency link resets the form state.
 */
export function CardDialog({ board, cardId, canWrite, onClose, onShowCard }: CardDialogProps) {
  const qc = useQueryClient();
  const allCards = useMemo(() => board.streams.flatMap((s) => s.cards), [board]);
  const streamName = useMemo(() => new Map(board.streams.map((s) => [s.id, s.name])), [board]);
  const card: CardDto | null = cardId === null ? null : allCards.find((c) => c.id === cardId) ?? null;
  const mode: 'create' | 'edit' | 'view' = !canWrite ? 'view' : card ? 'edit' : 'create';

  const [workStreamId, setWorkStreamId] = useState(String(board.streams[0]?.id ?? ''));
  const [title, setTitle] = useState(card?.title ?? '');
  const [description, setDescription] = useState(card?.description ?? '');
  const [roiOneTime, setRoiOneTime] = useState(numText(card?.roiOneTime));
  const [roiAnnual, setRoiAnnual] = useState(numText(card?.roiAnnual));
  const [roiLines, setRoiLines] = useState<RoiLineDraft[]>(() => draftsFromLines(card?.roiBreakdown));
  const [calculatorOpen, setCalculatorOpen] = useState((card?.roiBreakdown?.length ?? 0) > 0);
  const [roiExplanation, setRoiExplanation] = useState(card?.roiExplanation ?? '');
  const [tokensCost, setTokensCost] = useState(numText(card?.tokensCost));
  const [additionalCost, setAdditionalCost] = useState(numText(card?.additionalCost));
  const [workersNeeded, setWorkersNeeded] = useState(numText(card?.workersNeeded));
  const [estimateWeeks, setEstimateWeeks] = useState(numText(card?.estimateWeeks));
  const [productOwnerName, setProductOwnerName] = useState(card?.productOwnerName ?? '');
  const [dependencyCardId, setDependencyCardId] = useState(numText(card?.dependencyCardId));
  const [targetMonth, setTargetMonth] = useState(numText(card?.targetMonth));

  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const cardLabel = (c: CardDto) => `${c.title} (${streamName.get(c.workStreamId) ?? '?'})`;
  const dependencyOptions = [
    { value: '', label: 'None' },
    ...allCards
      .filter((c) => c.id !== cardId)
      .map((c) => ({ value: String(c.id), label: cardLabel(c) }))
      .sort((a, b) => a.label.localeCompare(b.label)),
  ];

  const onSaved = () => {
    void qc.invalidateQueries({ queryKey: boardQueryKey });
    onClose();
  };
  const onSaveError = (err: unknown) => {
    const next = saveErrorState(err, FIELDS);
    setFieldErrors(next.fieldErrors);
    setFormError(next.formError);
  };

  const save = useMutation({
    mutationFn: (dto: CardUpsertDto) => (card ? updateCard(card.id, dto) : createCard(dto)),
    onSuccess: onSaved,
    onError: onSaveError,
  });
  const remove = useMutation({
    mutationFn: (id: number) => deleteCard(id),
    onSuccess: onSaved,
    onError: (err) => {
      setConfirmDelete(false);
      setFormError(problemDetail(err, 'Delete failed. Please try again.'));
    },
  });

  // The card was removed (e.g. by another user) after the dialog opened.
  if (cardId !== null && !card) {
    return (
      <Modal open onClose={onClose} title="Card details" size="lg"
        footer={<button type="button" autoFocus onClick={onClose} className={btnSecondary}>Close</button>}>
        <p className="text-slate-700">This card no longer exists.</p>
      </Modal>
    );
  }

  const dependency = card?.dependencyCardId != null ? allCards.find((c) => c.id === card.dependencyCardId) : undefined;

  if (mode === 'view' && card) {
    return (
      <Modal open onClose={onClose} title="Card details" size="lg"
        footer={<button type="button" autoFocus onClick={onClose} className={btnSecondary}>Close</button>}>
        <dl className="flex flex-col gap-4">
          <ViewItem label="Title">{card.title}</ViewItem>
          <ViewItem label="Description" pre>{card.description}</ViewItem>
          <ViewItem label="ROI">{formatRoi(card)}</ViewItem>
          {card.roiBreakdown && card.roiBreakdown.length > 0 && (
            <div className="-mt-2"><RoiBreakdownTable lines={card.roiBreakdown} /></div>
          )}
          <ViewItem label="ROI explanation" pre>{card.roiExplanation}</ViewItem>
          <ViewItem label="Cost to build">
            {card.tokensCost === null && card.additionalCost === null ? null : (
              <>
                {formatBuildCost(buildCost(card))}
                <span className="block text-xs text-slate-500">
                  tokens {formatMoney(card.tokensCost ?? 0)} · additional {formatMoney(card.additionalCost ?? 0)}
                </span>
              </>
            )}
          </ViewItem>
          {card.expectedDate && <ViewItem label="Expected completion">{formatFullDate(card.expectedDate)}</ViewItem>}
          <div className="grid grid-cols-4 gap-3">
            <ViewItem label="Workers needed">{String(card.workersNeeded)}</ViewItem>
            <ViewItem label="Estimate (weeks)">{String(card.estimateWeeks)}</ViewItem>
            <ViewItem label="Product owner">{card.productOwnerName}</ViewItem>
            <ViewItem label="Month">{card.targetMonth === null ? null : <MonthPill card={card} />}</ViewItem>
          </div>
          <ViewItem label="Depends on">
            {dependency ? (
              <button type="button" className={linkButton} onClick={() => onShowCard(dependency.id)}>
                {dependency.title}
              </button>
            ) : (card.dependencyCardTitle ?? null)}
          </ViewItem>
        </dl>
      </Modal>
    );
  }

  function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setFieldErrors({});
    setFormError(null);
    save.mutate({
      workStreamId: card ? card.workStreamId : Number(workStreamId),
      title: title.trim(),
      description: textOrNull(description),
      roiOneTime: numOrNull(roiOneTime),
      roiAnnual: numOrNull(roiAnnual),
      roiBreakdown: roiLines.length === 0 ? null : roiLines.map(lineFromDraft),
      roiExplanation: textOrNull(roiExplanation),
      workersNeeded: workersNeeded.trim() === '' ? 0 : Number(workersNeeded),
      estimateWeeks: estimateWeeks.trim() === '' ? 0 : Number(estimateWeeks),
      productOwnerName: textOrNull(productOwnerName),
      dependencyCardId: dependencyCardId === '' ? null : Number(dependencyCardId),
      tokensCost: numOrNull(tokensCost),
      additionalCost: numOrNull(additionalCost),
      targetMonth: numOrNull(targetMonth),
    });
  }

  const selectedDependency = dependencyCardId === '' ? undefined : allCards.find((c) => String(c.id) === dependencyCardId);
  const busy = save.isPending || remove.isPending;

  const footer = (
    <>
      {mode === 'edit' && card && (
        <button
          type="button"
          disabled={busy}
          onClick={() => (confirmDelete ? remove.mutate(card.id) : setConfirmDelete(true))}
          className="mr-auto px-3 py-1.5 rounded text-red-700 hover:bg-red-50 disabled:opacity-50"
        >
          {confirmDelete ? 'Confirm delete' : 'Delete'}
        </button>
      )}
      <button type="button" onClick={onClose} className={btnSecondary}>Cancel</button>
      <button type="submit" form={FORM_ID} disabled={busy} className={btnPrimary}>
        {save.isPending ? 'Saving...' : 'Save'}
      </button>
    </>
  );

  return (
    <Modal open onClose={onClose} title={mode === 'create' ? 'New card' : 'Edit card'} size="xl" footer={footer}>
      <FormShell id={FORM_ID} onSubmit={onSubmit} formError={formError}>
        {mode === 'create' && (
          <Select
            label="Work stream"
            name="workStreamId"
            value={workStreamId}
            onChange={(e) => setWorkStreamId(e.target.value)}
            options={board.streams.map((s) => ({ value: String(s.id), label: s.name }))}
            errors={fieldErrors}
          />
        )}
        <Field label="Title" name="title" required maxLength={200} autoFocus value={title}
          onChange={(e) => setTitle(e.target.value)} errors={fieldErrors} />
        <TextArea label="Description" name="description" rows={4} value={description}
          onChange={(e) => setDescription(e.target.value)} errors={fieldErrors} />
        <div className="grid grid-cols-2 gap-3">
          <Field label="ROI one-time ($)" name="roiOneTime" id="roiOneTime" type="number" min={0} step={1} value={roiOneTime}
            onChange={(e) => setRoiOneTime(e.target.value)} errors={fieldErrors} />
          <Field label="ROI per year ($)" name="roiAnnual" id="roiAnnual" type="number" min={0} step={1} value={roiAnnual}
            onChange={(e) => setRoiAnnual(e.target.value)} errors={fieldErrors} />
        </div>
        <div className="flex flex-col gap-1">
          <span className="text-sm font-semibold text-slate-700">Cost to build</span>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Tokens needed ($)" name="tokensCost" id="tokensCost" type="number" min={0} step={1} value={tokensCost}
              onChange={(e) => setTokensCost(e.target.value)} errors={fieldErrors} />
            <Field label="Additional cost ($)" name="additionalCost" id="additionalCost" type="number" min={0} step={1}
              value={additionalCost} onChange={(e) => setAdditionalCost(e.target.value)} errors={fieldErrors} />
          </div>
        </div>
        <RoiCalculator
          open={calculatorOpen}
          onToggle={() => setCalculatorOpen((o) => !o)}
          lines={roiLines}
          onChange={setRoiLines}
          onUseTotals={(oneTime, annual) => {
            setRoiOneTime(String(oneTime));
            setRoiAnnual(String(annual));
          }}
          tokensCost={numOrNull(tokensCost)}
          additionalCost={numOrNull(additionalCost)}
          roiOneTime={numOrNull(roiOneTime)}
          roiAnnual={numOrNull(roiAnnual)}
        />
        <TextArea label="ROI explanation" name="roiExplanation" rows={3} value={roiExplanation}
          onChange={(e) => setRoiExplanation(e.target.value)} errors={fieldErrors} />
        {mode === 'edit' && card?.expectedDate && (
          <p className="text-sm text-slate-700" id="expected-completion">
            Expected completion: <strong>{formatFullDate(card.expectedDate)}</strong>{' '}
            <span className="text-slate-500">(set when the card entered Working)</span>
          </p>
        )}
        <div className="grid grid-cols-4 gap-3">
          <Field label="Workers needed" name="workersNeeded" type="number" min={0} step={1} value={workersNeeded}
            onChange={(e) => setWorkersNeeded(e.target.value)} errors={fieldErrors} />
          <Field label="Estimate (weeks)" name="estimateWeeks" type="number" min={0} step={0.5} value={estimateWeeks}
            onChange={(e) => setEstimateWeeks(e.target.value)} errors={fieldErrors} />
          <Field label="Product owner" name="productOwnerName" maxLength={100} value={productOwnerName}
            onChange={(e) => setProductOwnerName(e.target.value)} errors={fieldErrors} />
          <Select label="Month" name="targetMonth" value={targetMonth} options={monthOptions}
            onChange={(e) => setTargetMonth(e.target.value)} errors={fieldErrors} />
        </div>
        <div className="flex flex-col gap-1">
          <Select label="Depends on" name="dependencyCardId" value={dependencyCardId} options={dependencyOptions}
            onChange={(e) => setDependencyCardId(e.target.value)} errors={fieldErrors} />
          {selectedDependency && (
            <div className="text-sm text-slate-700">
              Depends on:{' '}
              <button type="button" className={linkButton} onClick={() => onShowCard(selectedDependency.id)}>
                {selectedDependency.title}
              </button>
            </div>
          )}
        </div>
      </FormShell>
    </Modal>
  );
}

function RoiBreakdownTable({ lines }: { lines: RoiLine[] }) {
  return (
    <table className="w-full text-sm text-slate-800 border-collapse">
      <caption className="sr-only">ROI breakdown</caption>
      <thead>
        <tr className="text-xs text-slate-500 text-left border-b border-slate-200">
          <th scope="col" className="py-1 pr-2 font-normal">Kind / label</th>
          <th scope="col" className="py-1 pr-2 font-normal text-right">Amount</th>
          <th scope="col" className="py-1 font-normal">Period</th>
        </tr>
      </thead>
      <tbody>
        {lines.map((line, i) => (
          <tr key={i} className="border-b border-slate-100">
            <td className="py-1 pr-2">
              {ROI_LINE_KIND_LABELS[line.kind] ?? line.kind}
              {line.label ? <span className="text-slate-500"> · {line.label}</span> : null}
            </td>
            <td className="py-1 pr-2 text-right whitespace-nowrap">{formatMoney(lineAmount(line))}</td>
            <td className="py-1">{ROI_PERIOD_LABELS[isComputedLabor(line) ? 'PerYear' : line.period] ?? line.period}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function ViewItem({ label, children, pre }: { label: string; children: ReactNode; pre?: boolean }) {
  const empty = children === null || children === undefined || children === '';
  return (
    <div>
      <dt className="text-xs text-slate-500">{label}</dt>
      <dd className={`text-sm text-slate-800 ${pre ? 'whitespace-pre-wrap' : ''}`}>{empty ? '-' : children}</dd>
    </div>
  );
}
